import { Fragment } from "react";
import { Listbox as HeadlessListbox, Transition } from "@headlessui/react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";

interface ListboxOption {
  label: string;
  upgradeDetails?: {
    minimumRequiredTier: string;
  };
  value: string;
}

interface ListboxProps<T extends ListboxOption> {
  className?: string;
  onChange: (option: T) => void;
  options: T[];
  value: T;
}

export function Listbox<T extends ListboxOption>({
  value,
  options,
  onChange,
  className,
}: ListboxProps<T>) {
  return (
    <HeadlessListbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <HeadlessListbox.Button
            className={classNames(
              "focus-within:bg-secondary border-dark flex items-center justify-between gap-x-2 border px-3 py-2",
              {
                "bg-secondary": open,
              },
              className,
            )}
          >
            <div className="flex-1 truncate text-left">
              <Typography.LabelSm weight="medium">
                {value.label}
              </Typography.LabelSm>
            </div>
            <Icon
              type="16-chevron-down"
              size={16}
              className={classNames(
                "shrink-0",
                open ? "text-darkest" : "text-default",
              )}
            />
          </HeadlessListbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <HeadlessListbox.Options className="bg-primary absolute !z-50 mt-1 max-h-96 w-full overflow-auto rounded-md py-2 shadow-2xl">
              {options.map(option => (
                <HeadlessListbox.Option
                  key={option.value}
                  value={option}
                  className="hover:text-dark hover:bg-secondary cursor-pointer list-none px-5 py-2"
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between">
                      <Typography.LabelSm
                        weight={selected ? "medium" : undefined}
                        color={selected ? "text-selector-active" : undefined}
                      >
                        {option.label}
                      </Typography.LabelSm>
                      {selected && (
                        <Icon
                          type="20-checkmark"
                          className="text-selector-active"
                        />
                      )}
                      {option.upgradeDetails && <Icon type="16-lock" />}
                    </div>
                  )}
                </HeadlessListbox.Option>
              ))}
            </HeadlessListbox.Options>
          </Transition>
        </div>
      )}
    </HeadlessListbox>
  );
}
